<!-- TRIGGER PILL (default) -->
<div *ngIf="variant === 'default'"
    class="vf-drp-trigger rounded-8 hover:border-gray-400 hover:bg-gray-50 px-10 f-size-14 py-2 text-vf-dark d-flex align-items-center cursor-pointer"
    [ngClass]="size === 'sm' ? 'border border-gray-300 h-30' : 'border-2 border-gray-300 h-35'"
    #triggerEl (click)="open($event)">
    <lucide-icon name="calendar" [size]="16" class="text-gray-500"></lucide-icon>

    <span *ngIf="!hasDates" class="text-gray-500">{{ placeholder }}</span>

    <span *ngIf="hasDates" class="text-vf-dark">{{ triggerText }}</span>

    <span *ngIf="hasDates" class="vf-drp-clear-icon d-flex align-items-center" (click)="clearInline($event)">
        <lucide-icon name="x" [size]="12" class="text-gray-400"></lucide-icon>
    </span>
</div>

<!-- TRIGGER FIELD (form input style) -->
<div *ngIf="variant === 'field'"
    class="vf-drp-trigger-field d-flex align-items-center border border-gray-200 rounded-8 px-12 h-40 cursor-pointer w-100 hover:border-gray-400"
    #triggerEl (click)="open($event)">
    <lucide-icon name="calendar" [size]="16" class="mr-2 text-gray-500"></lucide-icon>

    <span *ngIf="!hasDates" class="f-size-14 text-gray-400">{{ placeholder }}</span>

    <span *ngIf="hasDates" class="f-size-14 text-gray-700">{{ triggerText }}</span>

    <span *ngIf="hasDates" class="vf-drp-clear-icon d-flex align-items-center ml-auto" (click)="clearInline($event)">
        <lucide-icon name="x" [size]="12" class="text-gray-400"></lucide-icon>
    </span>
</div>

<!-- TRIGGER MINIMAL (borderless, lightweight) -->
<div *ngIf="variant === 'minimal'"
    class="vf-drp-trigger d-flex align-items-center gap-6 rounded-sm f-size-14 text-gray-500 hover:bg-gray-100 px-10 py-2 cursor-pointer text-nowrap h-36"
    #triggerEl (click)="open($event)">
    <lucide-icon name="calendar" [size]="14" class="text-gray-400"></lucide-icon>

    <span *ngIf="!hasDates" class="text-gray-500">{{ placeholder }}</span>

    <span *ngIf="hasDates">{{ triggerText }}</span>
</div>

<!-- TRIGGER ROW (inline field inside a details/row layout) -->
<div *ngIf="variant === 'row'"
    class="vf-drp-trigger-row d-flex align-items-center gap-1-5 px-8 f-size-14 text-gray-700 rounded-sm cursor-pointer select-none w-100 line-height-normal"
    #triggerEl (click)="open($event)">
    <span *ngIf="hasDates" class="d-flex align-items-center flex-1 line-height-normal" style="min-width: 0; gap: 4px;">
        <span class="text-truncate line-height-normal" *ngIf="startDateText">{{ startDateText }}</span>
        <span class="text-gray-400 line-height-normal" *ngIf="startDateText && endDateText" style="margin: 0 2px;">&rarr;</span>
        <span class="text-truncate line-height-normal" *ngIf="endDateText">{{ endDateText }}</span>
    </span>
    <span *ngIf="!hasDates" class="flex-1 text-left text-gray-400 line-height-normal" style="min-width: 0;">{{ placeholder }}</span>
    <lucide-icon name="chevron-down" [size]="16" class="text-gray-400 flex-shrink-0" [strokeWidth]="2" style="margin-left: auto;"></lucide-icon>
</div>

<!-- TRIGGER BUTTON (toolbar) -->
<button *ngIf="variant === 'toolbar'"
    class="vf-drp-trigger-toolbar btn tk-proxima-nova action-bar-button f-size-14 text-pure-white d-flex align-items-center hover:bg-blue-600"
    #triggerEl (click)="open($event)" type="button" [disabled]="disabled">
    <lucide-icon name="calendar-plus" class="mr-1" [size]="12" [strokeWidth]="2"></lucide-icon>
    {{ placeholder }}
</button>

<!-- POPOVER -->
<div *ngIf="isOpen" class="vf-drp-popover" [ngClass]="'vf-drp-popover--' + popoverAlign" #popoverEl (click)="$event.stopPropagation()">
    <!-- HEADER -->
    <div class="vf-drp-header">
        <div class="vf-drp-field" [class.vf-drp-field--active]="activeField === 'start'"
            (click)="onHeaderStartClick()">
            <span class="vf-drp-field-label">Start:</span>
            <span class="vf-drp-field-value" *ngIf="workingStart">{{ workingStart.format('MMM D, YYYY') }}</span>
            <span class="vf-drp-field-placeholder" *ngIf="!workingStart">Select date</span>
        </div>
        <div class="vf-drp-field" [class.vf-drp-field--active]="activeField === 'end'"
            (click)="onHeaderEndClick()">
            <span class="vf-drp-field-label">End:</span>
            <span class="vf-drp-field-value" *ngIf="workingEnd">{{ workingEnd.format('MMM D, YYYY') }}</span>
            <span class="vf-drp-field-placeholder" *ngIf="!workingEnd">Select date</span>
        </div>
        <div class="vf-drp-badge" *ngIf="durationDays !== null">
            {{ durationDays }} {{ durationDays === 1 ? 'day' : 'days' }}
        </div>
        <div class="vf-drp-badge" *ngIf="durationDays === null">
            &mdash; days
        </div>
    </div>

    <!-- BODY -->
    <div class="vf-drp-body">
        <!-- LEFT SIDEBAR: Presets -->
        <div class="vf-drp-presets">
            <button *ngFor="let preset of presets" class="vf-drp-preset-btn" type="button"
                (click)="applyPreset(preset)">
                {{ preset.label }}
            </button>
        </div>

        <!-- RIGHT: Calendar + Footer -->
        <div class="vf-drp-right">
            <div class="vf-drp-calendar">
                <!-- Month navigation -->
                <div class="vf-drp-month-nav">
                    <button class="vf-drp-nav-btn" type="button" (click)="prevMonth()">
                        <lucide-icon name="chevron-left" [size]="16"></lucide-icon>
                    </button>
                    <span class="vf-drp-month-label">{{ monthYearLabel }}</span>
                    <button class="vf-drp-nav-btn" type="button" (click)="nextMonth()">
                        <lucide-icon name="chevron-right" [size]="16"></lucide-icon>
                    </button>
                </div>

                <!-- Weekday headers -->
                <div class="vf-drp-weekdays">
                    <div *ngFor="let d of weekdays" class="vf-drp-weekday-cell">{{ d }}</div>
                </div>

                <!-- Day grid -->
                <div class="vf-drp-weeks">
                    <div *ngFor="let week of calendarWeeks" class="vf-drp-week">
                        <div *ngFor="let day of week"
                            class="vf-drp-day"
                            [ngClass]="getDayClasses(day)"
                            (click)="onDayClick(day)"
                            (mouseenter)="onDayHover(day)"
                            (mouseleave)="onDayLeave()">
                            <span *ngIf="day.isCurrentMonth">{{ day.dayOfMonth }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- FOOTER -->
            <div class="vf-drp-footer">
                <button class="vf-drp-clear-btn" type="button" (click)="clearAndEmit()">Clear</button>
            </div>
        </div>
    </div>
</div>
